import { formatAprValue } from "rujira.ui";
import clsx from "clsx";
import { FC, Suspense, useEffect, useMemo } from "react";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import { Link } from "react-router-dom";
import { graphql } from "relay-runtime";
import {
  AssetLabel,
  Button,
  Decimal,
  Fiat,
  formatApr,
  IconDenom,
  Icons,
  Progress,
  SortDir,
  SortableTableHeader,
  TableFrame,
  TablePagination,
  TranslationProvider,
  compareBigInts,
  compareNullableBigInts,
  compareNullableNumbers,
  compareStrings,
  classApr,
  useTablePagination,
  useTableSort,
  useGlobalModalContext,
  useLocale,
} from "rujira.ui";
import { formatBigintPercent } from "../common/bigint";
import { SortBar } from "../common/components/SortBar";
import { StickyBar } from "../common/components/StickyBar";
import { useListTopScroll } from "../hooks/useListTopScroll";
import { useIsMobile } from "../hooks/useBreakpoint";
import {
  BalanceProvider,
  usePreloadedBalances,
} from "../common/components/Balance";
import { lendRoute } from "../lending/routes";
import { usePreloadedAccountData } from "../services/accountData";
import { GhostVaultWithdraw } from "../strategies/components/GhostVaultBalance";
import { GhostVaultDeposit } from "../strategies/components/GhostVaultDeposit";
import { GhostVaultBalanceWithdrawFragment$key } from "../strategies/components/__generated__/GhostVaultBalanceWithdrawFragment.graphql";
import { DebtsAccountFragment$key } from "./__generated__/DebtsAccountFragment.graphql";
import { DebtsQuery } from "./__generated__/DebtsQuery.graphql";
import { Graph } from "../merge/components/Graph";
import { MarketCard } from "./components/mobile/MarketCard";
import {
  MarketCards,
  MarketCardsFallback,
} from "./components/mobile/MarketCards";
import { borrowedUsd, capPercent } from "./markets/derive";
import {
  CapData,
  CollateralConfig,
  VaultNode,
  VaultStrategy,
} from "./markets/types";

const MARKET_SORT_KEYS = [
  "symbol",
  "deposits",
  "borrowed",
  "utilization",
  "cap",
  "lendApr",
  "borrowApr",
  "myBalance",
] as const;
type SortKey = (typeof MARKET_SORT_KEYS)[number];

const LENDING_COLUMNS = [
  { id: "logo", width: "2.5rem" },
  { id: "token", width: "calc(16% - 2.5rem)", minWidth: "8rem" },
  { id: "deposits", width: "12%", minWidth: "8rem" },
  { id: "borrowed", width: "12%", minWidth: "8rem" },
  { id: "utilization", width: "10%", minWidth: "7rem" },
  { id: "cap", width: "11%", minWidth: "7rem" },
  { id: "lendApr", width: "9%", minWidth: "6rem" },
  { id: "borrowApr", width: "9%", minWidth: "6rem" },
  { id: "myBalance", width: "11%", minWidth: "7rem" },
  { id: "action", width: "10%", minWidth: "7rem" },
] as const;

const MARKET_SORTS: { key: SortKey; label: string }[] = [
  { key: "symbol", label: "Token" },
  { key: "deposits", label: "Total Deposits" },
  { key: "borrowed", label: "Total Borrowed" },
  { key: "utilization", label: "Utilization" },
  { key: "cap", label: "Borrow Cap" },
  { key: "lendApr", label: "Lending APR" },
  { key: "borrowApr", label: "Borrow APR" },
  { key: "myBalance", label: "My Balance" },
];
const RIGHT_ALIGNED_SORT_KEYS: readonly SortKey[] = [
  "lendApr",
  "borrowApr",
  "myBalance",
];
const query = graphql`
  query DebtsQuery {
    ghostCredit {
      collaterals {
        ratio
        asset {
          metadata {
            symbol
          }
        }
      }
      vaults {
        borrower {
          limit
          current
          vault {
            address
          }
        }
      }
    }
    strategies(first: 100, typenames: ["GhostVault"]) {
      edges {
        node {
          __typename
          ... on GhostVault {
            id
            address
            asset {
              asset
              type
              chain
              metadata {
                symbol
                decimals
              }
              variants {
                native {
                  denom
                }
              }
              price {
                current
              }
            }
            status {
              utilizationRatio
              debtPool {
                size
              }
              depositPool {
                size
              }
              valueUsd
              apr {
                status
                value
              }
              debtRate
              lendRate
            }
          }
        }
      }
    }
  }
`;

const accountFragment = graphql`
  fragment DebtsAccountFragment on Account {
    strategies {
      ... on GhostVaultAccount {
        id
        vault {
          address
        }
        receiptValue: value {
          amount
          asset {
            metadata {
              symbol
              decimals
            }
            price {
              current
            }
          }
        }
        valueUsd
        ...GhostVaultBalanceWithdrawFragment
      }
    }
  }
`;

export const DebtsFallback: FC = () => {
  const { sortBy, sortDir, toggleSort } = useLendingSort();

  return (
    <TableFrame
      className="lending__table"
      columns={LENDING_COLUMNS}
      stickyFirstColumn
      tableClassName="table table--fixed table--big condensed mb-0">
      <LendingTableHead
        sortBy={sortBy}
        sortDir={sortDir}
        toggleSort={toggleSort}
      />
      <tbody>
        {[...Array(4)].map((_, i) => (
          <tr key={`skeleton_${i}`}>
            <td className="lending__logo-cell" aria-hidden="true">
              <div className="icon-denom skeleton br-2" />
            </td>
            <td className="lending__token-cell">
              <div className="ml-1 skeleton h-1.5 w-6 br-2" />
            </td>
            <td className="text-right">
              <div className="skeleton h-1.5 w-8 br-2 ml-a" />
            </td>
            <td>
              <div className="skeleton h-1.5 w-8 br-2" />
            </td>
            <td>
              <div className="skeleton h-1.5 w-6 br-2" />
            </td>
            <td>
              <div className="skeleton h-1.5 w-5 br-2 ml-a" />
            </td>
            <td>
              <div className="skeleton h-1.5 w-5 br-2 ml-a" />
            </td>
            <td>
              <div className="skeleton h-1.5 w-5 br-2 ml-a" />
            </td>
            <td>
              <div className="skeleton h-1.5 w-8 br-2" />
            </td>
            <td />
          </tr>
        ))}
      </tbody>
    </TableFrame>
  );
};

export const Debts: FC = () => {
  const isMobile = useIsMobile();
  const [q, load] = useQueryLoader<DebtsQuery>(query);
  useEffect(() => {
    load({});
  }, []);
  const fallback = isMobile ? <MarketCardsFallback /> : <DebtsFallback />;
  if (!q) return fallback;
  return (
    <Suspense fallback={fallback}>
      <Content q={q} />
    </Suspense>
  );
};

const Content: FC<{ q: PreloadedQuery<DebtsQuery> }> = ({ q }) => {
  const data = usePreloadedQuery<DebtsQuery>(query, q);
  const { accountData } = usePreloadedAccountData();
  const account = useFragment<DebtsAccountFragment$key>(
    accountFragment,
    accountData ?? null
  );

  const collaterals = data.ghostCredit?.collaterals || [];
  const caps = data.ghostCredit?.vaults || [];
  const accountStrategies = account?.strategies || [];

  const isMobile = useIsMobile();
  const { sortBy: sortKey, sortDir, toggleSort } = useLendingSort();
  const { anchor } = useListTopScroll(isMobile, [sortKey, sortDir]);

  const vaults = useMemo(
    () =>
      (data.strategies.edges ?? []).flatMap((edge) =>
        edge?.node?.__typename === "GhostVault" ? [edge.node] : []
      ),
    [data.strategies.edges]
  );
  const capsByVaultAddress = useMemo(
    () =>
      new Map(caps.map(({ borrower }) => [borrower.vault.address, borrower])),
    [caps]
  );
  const strategiesByVaultAddress = useMemo(
    () =>
      new Map(
        accountStrategies.flatMap((strategy) =>
          strategy.vault ? [[strategy.vault.address, strategy] as const] : []
        )
      ),
    [accountStrategies]
  );
  const sortedVaults = useMemo(
    () =>
      [...vaults].sort((a, b) =>
        compareLendingVaults(
          a,
          b,
          capsByVaultAddress,
          strategiesByVaultAddress,
          sortKey,
          sortDir
        )
      ),
    [capsByVaultAddress, sortDir, sortKey, strategiesByVaultAddress, vaults]
  );
  const { pageRows: visibleVaults, pagination } = useTablePagination(
    sortedVaults,
    { resetKey: `${sortKey}:${sortDir}` }
  );

  const rows = visibleVaults.map((vault) => {
    const cap = capsByVaultAddress.get(vault.address);
    const myStrategy = strategiesByVaultAddress.get(vault.address);
    return (
      <Row
        key={vault.id}
        asCard={isMobile}
        vault={vault}
        cap={cap}
        collaterals={collaterals}
        myStrategy={myStrategy}
      />
    );
  });

  if (isMobile)
    return (
      <>
        <div ref={anchor} aria-hidden className="scroll-anchor" />
        <StickyBar
          collapseLabel="Show Sorter"
          className="sticky-bar--clear px-2 py-2">
          <SortBar
            options={MARKET_SORTS}
            sortKey={sortKey}
            sortDir={sortDir}
            onKeyChange={toggleSort}
            onDirToggle={() => toggleSort(sortKey)}
          />
        </StickyBar>
        <MarketCards>{rows}</MarketCards>
        <TablePagination {...pagination} />
      </>
    );

  return (
    <>
      <TableFrame
        className="lending__table"
        columns={LENDING_COLUMNS}
        stickyFirstColumn
        tableClassName="table table--fixed table--big condensed mb-0">
        <LendingTableHead
          sortBy={sortKey}
          sortDir={sortDir}
          toggleSort={toggleSort}
        />
        <tbody>{rows}</tbody>
      </TableFrame>
      <TablePagination {...pagination} />
    </>
  );
};

const LendingTableHead: FC<{
  sortBy: SortKey;
  sortDir: SortDir;
  toggleSort: (value: SortKey) => void;
}> = ({ sortBy, sortDir, toggleSort }) => (
  <thead>
    <tr>
      <th aria-hidden="true" />
      {MARKET_SORTS.map(({ key, label }) => (
        <SortableTableHeader
          key={key}
          label={label}
          value={key}
          active={sortBy}
          dir={sortDir}
          onSort={toggleSort}
          align={RIGHT_ALIGNED_SORT_KEYS.includes(key) ? "right" : "left"}
        />
      ))}
      <th aria-label="Actions" />
    </tr>
  </thead>
);

const useLendingSort = () =>
  useTableSort<SortKey>({
    initialBy: "lendApr",
    initialDir: SortDir.Desc,
    defaultDir: defaultLendingSortDir,
  });

const defaultLendingSortDir = (value: SortKey): SortDir =>
  value === "symbol" ? SortDir.Asc : SortDir.Desc;

const compareLendingVaults = (
  a: VaultNode,
  b: VaultNode,
  capsByVaultAddress: ReadonlyMap<string, CapData>,
  strategiesByVaultAddress: ReadonlyMap<string, VaultStrategy>,
  sortBy: SortKey,
  sortDir: SortDir
): number => {
  const result = (() => {
    switch (sortBy) {
      case "symbol":
        return compareStrings(
          a.asset.metadata.symbol,
          b.asset.metadata.symbol,
          sortDir
        );
      case "deposits":
        return compareBigInts(a.status.valueUsd, b.status.valueUsd, sortDir);
      case "borrowed":
        return compareBigInts(
          borrowedUsd(a.status),
          borrowedUsd(b.status),
          sortDir
        );
      case "utilization":
        return compareBigInts(
          a.status.utilizationRatio,
          b.status.utilizationRatio,
          sortDir
        );
      case "cap":
        return compareNullableNumbers(
          lendingCapPercent(capsByVaultAddress.get(a.address)),
          lendingCapPercent(capsByVaultAddress.get(b.address)),
          sortDir
        );
      case "lendApr":
        return compareNullableBigInts(
          a.status.apr.value,
          b.status.apr.value,
          sortDir
        );
      case "borrowApr":
        return compareBigInts(a.status.debtRate, b.status.debtRate, sortDir);
      case "myBalance":
        return compareBigInts(
          strategiesByVaultAddress.get(a.address)?.valueUsd ?? 0n,
          strategiesByVaultAddress.get(b.address)?.valueUsd ?? 0n,
          sortDir
        );
      default:
        return 0;
    }
  })();

  return (
    result || compareStrings(a.asset.metadata.symbol, b.asset.metadata.symbol)
  );
};

const lendingCapPercent = (cap: CapData | undefined): number | null =>
  cap && cap.limit > 0n ? capPercent(cap) : null;

const Row: FC<{
  vault: VaultNode;
  cap: CapData | undefined;
  collaterals?: readonly CollateralConfig[];
  myStrategy?: VaultStrategy;
  asCard?: boolean;
}> = ({ vault, cap, myStrategy, asCard }) => {
  const { showModal, hideModal } = useGlobalModalContext();
  const { toRoot } = useLocale();
  const { asset, status } = vault;
  const url = toRoot(lendRoute(asset));

  const depositUsd = status.valueUsd;
  const borrowed = borrowedUsd(status);
  const capPct = capPercent(cap);

  const myDeposit = myStrategy?.receiptValue?.amount ?? 0n;
  const myDepositUsd = myStrategy?.valueUsd ?? 0n;

  const balances = usePreloadedBalances();
  const hasBalance = balances.some(
    (b) => b.asset.asset === asset.asset && b.balance > 0n
  );

  const openLend = () => {
    showModal({
      backgroundClose: false,
      children: (
        <TranslationProvider namespace="strategies">
          <BalanceProvider asset={asset}>
            <GhostVaultDeposit
              asset={asset}
              address={vault.address}
              currentBalance={myDeposit}
            />
          </BalanceProvider>
        </TranslationProvider>
      ),
    });
  };

  const openWithdraw = () => {
    if (!myStrategy) return;
    showModal({
      backgroundClose: false,
      children: (
        <TranslationProvider namespace="strategies">
          <Suspense>
            <GhostVaultWithdraw
              k={myStrategy as GhostVaultBalanceWithdrawFragment$key}
              cancel={hideModal}
            />
          </Suspense>
        </TranslationProvider>
      ),
    });
  };

  if (asCard)
    return (
      <MarketCard
        vault={vault}
        cap={cap}
        myStrategy={myStrategy}
        hasBalance={hasBalance}
        url={url}
        onDeposit={openLend}
        onWithdraw={openWithdraw}
      />
    );

  return (
    <tr>
      <td className="lending__logo-cell" aria-hidden="true">
        <IconDenom denom={asset.metadata.symbol} className="w-4 block" />
      </td>
      <td className="lending__token-cell">
        <Link
          to={url}
          state={{ from: "lend" }}
          className="flex ai-c no-underline color-white w-full">
          <h3 className="condensed fs-22 lh-22 fs-lg-28 lh-lg-28 fw-400 mb-0 ml-1 nowrap">
            <AssetLabel
              asset={asset}
              Container={({ children }) => (
                <small className="color-grey fs-12 fs-lg-14">{children}</small>
              )}
            />
          </h3>
        </Link>
      </td>

      <td>
        <div className="flex dir-c">
          <Decimal
            amount={status.depositPool.size}
            symbol={asset.metadata.symbol}
            decimals={asset.metadata.decimals}
            className="fs-16 condensed fw-500"
            clip
          />
          <Fiat
            amount={depositUsd}
            symbol="$"
            decimals={8}
            className="color-grey fs-12 condensed"
            clip
          />
        </div>
      </td>

      <td>
        <div className="flex dir-c">
          <Decimal
            amount={status.debtPool.size}
            symbol={asset.metadata.symbol}
            decimals={asset.metadata.decimals}
            className="fs-16 condensed fw-500"
            clip
          />
          <Fiat
            amount={borrowed}
            symbol="$"
            decimals={8}
            className="color-grey fs-12 condensed"
            clip
          />
        </div>
      </td>

      <td>
        <div className="lending__graph-container">
          <Graph
            percentage={Number(status.utilizationRatio) / 10_000_000_000}
            width={50}
          />
          <span className="lending__graph-container-pct">
            {formatBigintPercent(status.utilizationRatio)}
          </span>
        </div>
      </td>

      <td>
        {cap ? (
          <>
            <Decimal
              amount={cap.limit}
              symbol={asset.metadata.symbol}
              decimals={asset.metadata.decimals}
              className="fs-16 condensed fw-500"
              clip
            />
            <Progress percentage={capPct} height={3} className="mt-1" />
          </>
        ) : (
          <span className="color-grey fs-12">—</span>
        )}
      </td>

      {/* <td className="text-right">
        {(() => {
          const c = collaterals.find(
            (c) => c.asset.metadata.symbol === asset.metadata.symbol
          );
          return c ? (
            <>{formatBigintPercent(c.ratio)}</>
          ) : (
            <span className="color-grey">—</span>
          );
        })()}
      </td> */}

      <td className={`text-right ${classApr(status.apr)}`}>
        {formatApr(status.apr)}
      </td>

      <td className="text-right">{formatAprValue(status.debtRate)}</td>

      <td className="text-right">
        {myDeposit > 0n ? (
          <div className="flex dir-c ai-e">
            <Decimal
              amount={myDeposit}
              symbol={
                myStrategy?.receiptValue?.asset.metadata.symbol ||
                asset.metadata.symbol
              }
              decimals={
                myStrategy?.receiptValue?.asset.metadata.decimals ??
                asset.metadata.decimals
              }
              className="fs-16 condensed fw-500"
              clip
            />
            <Fiat
              amount={myDepositUsd}
              symbol="$"
              decimals={8}
              className="color-grey fs-12 condensed"
              clip
            />
          </div>
        ) : (
          <span className="color-grey fs-12">—</span>
        )}
      </td>

      <td>
        <div
          className={clsx("flex dir-c gap-0.5", {
            "gap-1": myDeposit > 0n,
          })}>
          {myDeposit > 0n && (
            <Button
              className="button--xs button--outline button--blue w-full"
              label="Withdraw"
              onClick={openWithdraw}
            />
          )}
          <Button
            className={clsx("button--xs w-full", {
              "button--outline": !hasBalance,
            })}
            disabled={!hasBalance}
            label="Deposit"
            onClick={openLend}
          />
          <Link
            to={url}
            state={{ from: "lend" }}
            className="button button--xs button--outline button--grey w-full button--icon-right">
            Details
            <Icons.ArrowUpRight />
          </Link>
        </div>
      </td>
    </tr>
  );
};
